import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';

import MathEditor from './MathEditor';
import { handleSaveFormula } from './utils/Form';
import { addBorder, editExpression, removeBorder } from './utils/Editor';

import './index.css';

function rawValueToQuillDelta(rawValue) {
  const raw = String(rawValue ?? '');

  // Strip markdown image markers — they're served via the question pane.
  let normalised = raw.replace(/!\[[^\]]*\]\([^)]+\)/g, '');

  // Normalise $$...$$ display math to $...$ inline math so that both
  // render uniformly as Quill formula embeds (otherwise the raw $$
  // markers show up as ugly literal text in the editor).
  normalised = normalised.replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => {
    return `$${String(latex).trim()}$`;
  });

  const ops = [];

  // Turn $...$ math segments into Quill `formula` embeds.
  const regex = /\$([^$]+?)\$/gs;
  let lastIndex = 0;
  let m = regex.exec(normalised);
  while (m) {
    if (m.index > lastIndex) {
      ops.push({ insert: normalised.slice(lastIndex, m.index) });
    }

    const latex = String(m[1] ?? '').trim();
    if (latex) {
      ops.push({ insert: { formula: latex } });
    } else {
      ops.push({ insert: '$$' });
    }

    lastIndex = regex.lastIndex;
    m = regex.exec(normalised);
  }

  if (lastIndex < normalised.length) {
    ops.push({ insert: normalised.slice(lastIndex) });
  }

  // Quill expects at least one trailing newline.
  if (ops.length === 0) return { ops: [{ insert: '\n' }] };

  const lastOp = ops[ops.length - 1];
  const lastIsNewline = typeof lastOp.insert === 'string' && lastOp.insert.endsWith('\n');
  if (!lastIsNewline) ops.push({ insert: '\n' });

  return { ops };
}

function quillDeltaToRawValue(delta) {
  const ops = delta?.ops || [];
  let raw = '';

  for (const op of ops) {
    if (typeof op?.insert === 'string') raw += op.insert;
    else if (op?.insert && typeof op.insert === 'object' && op.insert.formula != null) raw += `$${op.insert.formula}$`;
  }

  // Strip exactly one trailing newline added by Quill.
  if (raw.endsWith('\n')) raw = raw.slice(0, -1);
  return raw;
}

const Editor = ({ rawValue = '', readOnly = false, placeholder = 'Write something...', onRawValueChange }) => {
  const [editorHtml, setEditorHtml] = useState('');
  const [mathEditorVisible, setMathEditorVisible] = useState(false);
  const [expressionEditable, setExpressionEditable] = useState(false);

  const quillRef = useRef(null);
  const isSettingContentsRef = useRef(false);
  const lastRawValueRef = useRef(rawValue);
  const didInitContentsRef = useRef(false);

  const modules = useMemo(() => {
    const base = { clipboard: { matchVisual: false }, formula: true };
    if (readOnly) return base;
    return {
      ...base,
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ header: [1, 2, 3, false] }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['link', 'image', 'formula'],
      ],
    };
  }, [readOnly]);

  const toggleMathEditor = () => {
    if (readOnly) return;
    setMathEditorVisible((prevVisible) => !prevVisible);
    setExpressionEditable(() => false);
  };

  useEffect(() => {
    if (readOnly) return;
    editExpression(setMathEditorVisible, setExpressionEditable);
  }, [readOnly, editorHtml]);

  useEffect(() => {
    const tooltipElement = document.querySelector('.ql-tooltip');
    if (tooltipElement) tooltipElement.style.opacity = '0';

    const formulaButton = document.querySelector('button.ql-formula');
    if (formulaButton) formulaButton.addEventListener('click', toggleMathEditor);

    return () => {
      if (formulaButton) formulaButton.removeEventListener('click', toggleMathEditor);
      const expressions = document.querySelectorAll('span.ql-formula');
      expressions.forEach((expression) => {
        expression.removeEventListener('mouseenter', addBorder);
        expression.removeEventListener('mouseleave', removeBorder);
      });
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const attemptInit = (triesLeft) => {
      if (cancelled) return;

      const reactQuill = quillRef.current;
      const quill = reactQuill?.getEditor ? reactQuill.getEditor() : null;
      if (!quill) {
        if (triesLeft > 0) requestAnimationFrame(() => attemptInit(triesLeft - 1));
        return;
      }

      if (didInitContentsRef.current && rawValue === lastRawValueRef.current) return;
      lastRawValueRef.current = rawValue;

      isSettingContentsRef.current = true;
      const delta = rawValueToQuillDelta(rawValue);
      quill.setContents(delta, 'silent');
      setEditorHtml(quill.root.innerHTML);
      isSettingContentsRef.current = false;
      didInitContentsRef.current = true;
    };

    attemptInit(20);

    return () => {
      cancelled = true;
    };
  }, [rawValue]);

  const handleChange = (html) => {
    if (isSettingContentsRef.current) return;
    setEditorHtml(html);

    if (readOnly) return;
    if (!onRawValueChange) return;

    const reactQuill = quillRef.current;
    const quill = reactQuill?.getEditor ? reactQuill.getEditor() : null;
    if (!quill) return;

    const delta = quill.getContents();
    onRawValueChange(quillDeltaToRawValue(delta));
  };

  return (
    <div className="app">
      <ReactQuill
        ref={quillRef}
        onChange={handleChange}
        value={editorHtml}
        modules={modules}
        bounds=".app"
        readOnly={readOnly}
        placeholder={placeholder}
      />
      {!readOnly && mathEditorVisible && (
        <MathEditor
          onSaveFormula={handleSaveFormula}
          setMathEditorVisible={setMathEditorVisible}
          expressionEditable={expressionEditable}
          setExpressionEditable={setExpressionEditable}
        />
      )}
    </div>
  );
};

export default Editor;

